import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button, message, Cascader, DatePicker, Tooltip, Select } from "antd";
import { CloseOutlined, MailOutlined, PrinterOutlined, PlusOutlined, DeleteOutlined, SaveOutlined, EditOutlined, CheckOutlined } from "@ant-design/icons";
import { CheckmarkCircle02Icon } from "hugeicons-react";
import dayjs from "dayjs";
import QRCode from "qrcode";
import { useSelector } from "react-redux";
import { API_BASE_URL, FRONTEND_BASE_URL } from "../../../config/api";
import { createInvoice, updateInvoice, saveCompanyGst } from "../../../services/invoiceService";
import { getCompanySettings } from "../../../services/organizationService";
import { getLeadById } from "../../../services/leadService";
import { buildProductCascaderOptions, cascaderSearchFilter, resolveCascaderProduct } from "../../../utils/productCatalogUtils";
import { generatePdfFromElement } from "../../../utils/pdfExportUtils";
import { getStateInfoFromGstin, isInterStateSupply, getStateCodeFromName } from "../../../utils/gstUtils";
import MailComposeDrawer from "../mail/MailComposeDrawer";
import "./invoice.css";

const resolveAssetUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  if (trimmed.startsWith("/")) {
    return trimmed;
  }
  return `${API_BASE_URL}/${trimmed}`;
};

// Convert number to Indian currency words
function numberToWordsIndian(num) {
  if (!num || Number.isNaN(Number(num))) return "Rupees Zero Only";
  const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function inWords(val) {
    const nStr = String(val);
    if (nStr.length > 9) return "overflow";
    const nArr = /^(\d{2})(\d{2})(\d{2})(\d)(\d{2})$/.exec(("000000000" + nStr).slice(-9));
    if (!nArr) return "";
    let str = "";
    if (Number(nArr[1]) !== 0) {
      str += (a[Number(nArr[1])] || `${b[nArr[1][0]]} ${a[nArr[1][1]]}`) + "Crore ";
    }
    if (Number(nArr[2]) !== 0) {
      str += (a[Number(nArr[2])] || `${b[nArr[2][0]]} ${a[nArr[2][1]]}`) + "Lakh ";
    }
    if (Number(nArr[3]) !== 0) {
      str += (a[Number(nArr[3])] || `${b[nArr[3][0]]} ${a[nArr[3][1]]}`) + "Thousand ";
    }
    if (Number(nArr[4]) !== 0) {
      str += (a[Number(nArr[4])] || `${b[nArr[4][0]]} ${a[nArr[4][1]]}`) + "Hundred ";
    }
    const tensVal = Number(nArr[5]);
    if (tensVal !== 0) {
      const prefix = str !== "" ? "and " : "";
      const word = a[tensVal] || `${b[nArr[5][0]]} ${a[nArr[5][1]]}`;
      str += `${prefix}${word}`;
    }
    return str.trim();
  }

  const rounded = Math.round(Number(num));
  const words = inWords(rounded);
  return words ? `Rupees ${words} Only` : "Rupees Zero Only";
}

const getDraftInvoiceNumber = (issuedDate) => {
  const date = issuedDate ? new Date(issuedDate) : new Date();
  const yy = String(date.getFullYear()).slice(-2);
  return `INV${yy}Draft`;
};

const parseTerms = (terms) => {
  if (Array.isArray(terms)) return terms;
  if (typeof terms === "string" && terms.trim()) {
    return terms
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
  }
  return [];
};

const parseLeadAddress = (l) => {
  if (!l) return "";
  return l.address || l.registeredAddress || [l.streetAddressLine1, l.streetAddressLine2, l.city, l.state, l.postalCode].filter(Boolean).join(", ") || l.city || "";
};

const findMatchingProduct = (dbProducts, dealName) => {
  if (!dealName || !Array.isArray(dbProducts) || dbProducts.length === 0) return null;
  const target = dealName.trim().toLowerCase();
  for (const p of dbProducts) {
    const pName = (p.name || "").trim().toLowerCase();
    if (pName === target || target.includes(pName) || pName.includes(target)) {
      return p;
    }
    if (Array.isArray(p.subProducts)) {
      for (const sp of p.subProducts) {
        const spName = (sp.name || "").trim().toLowerCase();
        const combined = `${pName} - ${spName}`.toLowerCase();
        if (spName === target || combined === target || target.includes(spName)) {
          return {
            ...sp,
            name: `${p.name} - ${sp.name}`,
            displayName: `${p.name} - ${sp.name}`,
            price: sp.price || sp.minPrice || p.minPrice || 0,
            sacHsn: sp.sacHsn || p.sacHsn || "",
          };
        }
      }
    }
  }
  return null;
};

const applyDbSettings = (s, setters) => {
  if (!s) return;
  const { setDbProducts, setDbTerms, setDbBankDetails, setDbBankDetails2, setDbContactDetails, setDbGstPercentage, setDbGstNumber, setDbStateName, setDbStateCode, setDbCompanyLogo, setLogoLoadError, setDbCompanySeal, setSealLoadError, setDbCompanyName, setDbRegisteredAddress } = setters;

  setDbProducts(Array.isArray(s.products) ? s.products : []);
  setDbTerms(parseTerms(s.invoiceTerms));
  if (s.bankDetails) setDbBankDetails(s.bankDetails);
  if (s.bankDetails2) setDbBankDetails2(s.bankDetails2);
  if (s.contactDetails) setDbContactDetails(s.contactDetails);
  if (s.gstPercentage !== undefined) setDbGstPercentage(s.gstPercentage);
  if (s.gstNumber) setDbGstNumber(s.gstNumber);
  const derivedComp = s.gstNumber ? getStateInfoFromGstin(s.gstNumber) : { stateCode: "", stateName: "" };
  if (setDbStateName) setDbStateName(s.stateName || s.state || derivedComp.stateName || "");
  if (setDbStateCode) setDbStateCode(s.stateCode || derivedComp.stateCode || "");
  if (s.invoiceLogo) {
    setDbCompanyLogo(s.invoiceLogo);
    setLogoLoadError(false);
  }
  if (s.invoiceSeal) {
    setDbCompanySeal(s.invoiceSeal);
    setSealLoadError(false);
  }
  if (s.companyName) setDbCompanyName(s.companyName);
  if (s.invoiceRegisteredAddress || s.companyAddress) {
    setDbRegisteredAddress(s.invoiceRegisteredAddress || s.companyAddress);
  }
};

const enrichClientDetails = (lead, initialValues, setters) => {
  if (!lead) return;
  const leadAddr = parseLeadAddress(lead);
  if (!initialValues.initialAddr && leadAddr) setters.setClientAddress(leadAddr);
  if (!initialValues.initialGst && (lead.gstNumber || lead.gstin)) {
    const gVal = lead.gstNumber || lead.gstin;
    setters.setClientGstNumber(gVal);
    const derived = getStateInfoFromGstin(gVal);
    if (!initialValues.initialState && derived.stateName && setters.setClientStateName) {
      setters.setClientStateName(derived.stateName);
    }
    if (!initialValues.initialStateCode && derived.stateCode && setters.setClientStateCode) {
      setters.setClientStateCode(derived.stateCode);
    }
  }
  if (!initialValues.initialState && lead.state && setters.setClientStateName) {
    setters.setClientStateName(lead.state);
  }
  if (!initialValues.initialContact && lead.contactName) setters.setClientContactName(lead.contactName);
  if (!initialValues.initialPhone && lead.contactNumber) setters.setClientPhone(lead.contactNumber);
  if (!initialValues.initialEmail && (lead.directorEmailAddress || lead.email)) {
    setters.setClientEmail(lead.directorEmailAddress || lead.email);
  }
};

const fetchLeadDetails = (docComp, lookupId, initialCompName, initialValues, setters) => {
  const needsEnrichment = (!initialValues.initialAddr || !initialValues.initialGst) && (lookupId || initialCompName) && docComp;
  if (!needsEnrichment) return;

  getLeadById(docComp, lookupId || initialCompName, { companyName: initialCompName })
    .then((res) => {
      if (res?.success && res.lead) {
        enrichClientDetails(res.lead, initialValues, setters);
      }
    })
    .catch((e) => console.warn("[InvoiceModal] Could not enrich activeRecord from lead:", e));
};

const initRecordData = (activeRecord, activeCompanyCode, setters) => {
  const { setInvoiceNumberStr, setInvoiceIssuedAt, setInvoiceDueDate, setInvoiceItems, setBaseAmountPaid, setAdditionalPayment, setPaymentMethod, setDocumentCompanyCode, setClientCompanyName, setClientContactName, setClientPhone, setClientEmail, setClientAddress, setClientGstNumber, setClientStateName, setClientStateCode } = setters;

  setInvoiceNumberStr(activeRecord.invoiceNumber || getDraftInvoiceNumber());
  setInvoiceIssuedAt(activeRecord.invoiceDate ? dayjs(activeRecord.invoiceDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"));
  setInvoiceDueDate(activeRecord.dueDate ? dayjs(activeRecord.dueDate).format("YYYY-MM-DD") : dayjs().add(15, "day").format("YYYY-MM-DD"));
  setInvoiceItems(activeRecord.items || []);
  setBaseAmountPaid(Number(activeRecord.amountPaid || 0));
  setAdditionalPayment(0);
  if (setPaymentMethod) setPaymentMethod(activeRecord.paymentMethod || "");
  const docComp = activeRecord.companyCode || activeCompanyCode;
  setDocumentCompanyCode(docComp);

  const initialCompName = activeRecord.leadCompanyName || activeRecord.clientSnapshot?.companyName || activeRecord.companyName || "";
  const initialContact = activeRecord.contactName || activeRecord.clientSnapshot?.contactName || "";
  const initialPhone = activeRecord.contactNumber || activeRecord.clientSnapshot?.phone || "";
  const initialEmail = activeRecord.directorEmailAddress || activeRecord.clientSnapshot?.email || "";
  const initialAddr = activeRecord.clientSnapshot?.address || activeRecord.address || "";
  const initialGst = activeRecord.clientSnapshot?.gstNumber || activeRecord.gstNumber || activeRecord.clientSnapshot?.gstin || "";
  const derivedClient = initialGst ? getStateInfoFromGstin(initialGst) : { stateCode: "", stateName: "" };
  const initialClientState = activeRecord.clientSnapshot?.stateName || activeRecord.clientSnapshot?.state || activeRecord.state || derivedClient.stateName || "Tamil Nadu";
  const initialClientStateCode = activeRecord.clientSnapshot?.stateCode || derivedClient.stateCode || (initialClientState === "Tamil Nadu" ? "33" : getStateCodeFromName(initialClientState)) || "33";

  setClientCompanyName(initialCompName);
  setClientContactName(initialContact);
  setClientPhone(initialPhone);
  setClientEmail(initialEmail);
  setClientAddress(initialAddr);
  setClientGstNumber(initialGst);
  if (setClientStateName) setClientStateName(initialClientState);
  if (setClientStateCode) setClientStateCode(initialClientStateCode);



  const initialValues = { initialAddr, initialGst, initialContact, initialPhone, initialEmail, initialState: initialClientState, initialStateCode: initialClientStateCode };
  const lookupId = activeRecord.leadId || activeRecord.clientId || activeRecord.clientSnapshot?.clientId || activeRecord.clientSnapshot?.leadId;
  fetchLeadDetails(docComp, lookupId, initialCompName, initialValues, setters);
};

const initNewLeadData = (activeLead, activeCompanyCode, dbProducts, setters) => {
  const { setInvoiceNumberStr, setInvoiceIssuedAt, setInvoiceDueDate, setDocumentCompanyCode, setBaseAmountPaid, setAdditionalPayment, setPaymentMethod, setClientCompanyName, setClientContactName, setClientPhone, setClientEmail, setClientAddress, setClientGstNumber, setClientStateName, setClientStateCode, setSelectedProduct, setItemPrice, setItemSacHsn, setItemQuantity, setInvoiceItems } = setters;

  const todayStr = dayjs().format("YYYY-MM-DD");
  setInvoiceNumberStr(getDraftInvoiceNumber(todayStr));
  setInvoiceIssuedAt(todayStr);
  setInvoiceDueDate(dayjs().add(15, "day").format("YYYY-MM-DD"));
  setDocumentCompanyCode(activeCompanyCode);
  setBaseAmountPaid(0);
  setAdditionalPayment(0);
  if (setPaymentMethod) setPaymentMethod("");

  const leadGst = activeLead?.gstNumber || activeLead?.gstin || activeLead?.gst || "";
  const derivedClient = leadGst ? getStateInfoFromGstin(leadGst) : { stateCode: "", stateName: "" };
  const leadState = activeLead?.state || derivedClient.stateName || "Tamil Nadu";
  const leadStateCode = activeLead?.stateCode || derivedClient.stateCode || (leadState === "Tamil Nadu" ? "33" : getStateCodeFromName(leadState)) || "33";

  setClientCompanyName(activeLead?.leadCompanyName || activeLead?.companyName || "");
  setClientContactName(activeLead?.contactName || activeLead?.primaryContact || "");
  setClientPhone(activeLead?.contactNumber || activeLead?.phone || "");
  setClientEmail(activeLead?.directorEmailAddress || activeLead?.email || "");
  setClientAddress(parseLeadAddress(activeLead));
  setClientGstNumber(leadGst);
  if (setClientStateName) setClientStateName(leadState);
  if (setClientStateCode) setClientStateCode(leadStateCode);

  const targetDealName = activeLead?.dealName || activeLead?.productName || activeLead?.service || "";
  const matchedProduct = findMatchingProduct(dbProducts, targetDealName);

  if (matchedProduct) {
    setSelectedProduct(matchedProduct);
    const rate = Number(activeLead?.amount) || matchedProduct.price || matchedProduct.minPrice || 0;
    const sacHsn = matchedProduct.sacHsn || "998313";
    setItemPrice(rate);
    setItemSacHsn(sacHsn);
    setItemQuantity(1);
    setInvoiceItems([
      {
        name: matchedProduct.name || "",
        sacHsn,
        quantity: 1,
        rate,
        taxable: rate,
        total: rate,
      },
    ]);
  } else {
    setSelectedProduct(null);
    setItemPrice(0);
    setItemSacHsn("—");
    setItemQuantity(1);
    setInvoiceItems([]);
  }
};

const checkHasCustomerGst = (clientGstNumber) => {
  if (!clientGstNumber) return false;
  const trimmed = String(clientGstNumber).trim();
  return Boolean(trimmed && trimmed !== "—" && trimmed !== "-");
};

const resolveEffectiveBankDetails = (activeRecord, hasCustomerGst, dbBankDetails, dbBankDetails2) => {
  if (activeRecord?.companySnapshot?.bankDetails?.bankName) {
    return activeRecord.companySnapshot.bankDetails;
  }
  if (!hasCustomerGst && dbBankDetails2?.bankName && dbBankDetails2?.accountNumber) {
    return dbBankDetails2;
  }
  return dbBankDetails;
};

const calculateInvoiceTotals = (invoiceItems, effectiveGstRate, isInterState = false) => {
  const subtotal = invoiceItems.reduce((acc, item) => {
    const lineTotal = (Number(item.quantity) || 1) * (Number(item.rate) || 0);
    return acc + lineTotal;
  }, 0);

  const gstRate = effectiveGstRate;
  const halfRate = gstRate / 2;
  const cgstAmount = isInterState ? 0 : (subtotal * halfRate) / 100;
  const sgstAmount = isInterState ? 0 : (subtotal * halfRate) / 100;
  const igstAmount = isInterState ? (subtotal * gstRate) / 100 : 0;
  const totalGst = isInterState ? igstAmount : cgstAmount + sgstAmount;
  const grandTotal = Math.round(subtotal + totalGst);

  return {
    subtotal,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalGst,
    grandTotal,
    halfRate,
    gstRate,
    isInterState,
    isExempt: effectiveGstRate === 0,
  };
};

const calculateGstBreakdown = (invoiceItems, calculations, effectiveGstRate, isInterState = false) => {
  if (calculations.isExempt || effectiveGstRate === 0) return [];
  const map = new Map();
  invoiceItems.forEach((it) => {
    const hsn = it.sacHsn || "998313";
    const taxable = (Number(it.quantity) || 1) * (Number(it.rate) || 0);

    if (isInterState) {
      const igstRate = effectiveGstRate;
      const igstAmount = (taxable * igstRate) / 100;
      const totalTax = igstAmount;

      if (map.has(hsn)) {
        const existing = map.get(hsn);
        existing.taxableValue += taxable;
        existing.igstAmount += igstAmount;
        existing.totalTax += totalTax;
      } else {
        map.set(hsn, {
          hsnSac: hsn,
          taxableValue: taxable,
          igstRate,
          igstAmount,
          totalTax,
          isInterState: true,
        });
      }
    } else {
      const cgstRate = calculations.halfRate;
      const sgstRate = calculations.halfRate;
      const cgstAmount = (taxable * cgstRate) / 100;
      const sgstAmount = (taxable * sgstRate) / 100;
      const totalTax = cgstAmount + sgstAmount;

      if (map.has(hsn)) {
        const existing = map.get(hsn);
        existing.taxableValue += taxable;
        existing.cgstAmount += cgstAmount;
        existing.sgstAmount += sgstAmount;
        existing.totalTax += totalTax;
      } else {
        map.set(hsn, {
          hsnSac: hsn,
          taxableValue: taxable,
          cgstRate,
          cgstAmount,
          sgstRate,
          sgstAmount,
          totalTax,
          isInterState: false,
        });
      }
    }
  });
  return Array.from(map.values());
};

const InvoiceSenderLogo = ({ logoUrl, logoLoadError, onLogoError, displayCompanyName }) => {
  const assetUrl = resolveAssetUrl(logoUrl);
  if (assetUrl && !logoLoadError) {
    return <img src={assetUrl} alt="Invoice logo" onError={onLogoError} className="sender-logo-img" />;
  }
  return <div className="sender-text-logo">{displayCompanyName.slice(0, 8)}</div>;
};

const InvoiceCompanySeal = ({ sealUrl, sealLoadError, onSealError, displayCompanyName }) => {
  const assetUrl = resolveAssetUrl(sealUrl);
  if (assetUrl && !sealLoadError) {
    return <img src={assetUrl} alt="Company Seal" onError={onSealError} className="signature-stamp-img" />;
  }
  return (
    <svg viewBox="0 0 120 120" width="85" height="85" className="signature-stamp">
      <circle cx="60" cy="60" r="50" fill="none" stroke="#2563eb" strokeWidth="1.5" strokeDasharray="3 3" />
      <circle cx="60" cy="60" r="45" fill="none" stroke="#2563eb" strokeWidth="1.2" />
      <text fill="#2563eb" fontSize="7.5" fontFamily="monospace" letterSpacing="1" textAnchor="middle" x="60" y="24">
        ★ SIGNATURE ★
      </text>
      <text fill="#2563eb" fontSize="7" fontFamily="monospace" letterSpacing="1" textAnchor="middle" x="60" y="104">
        OFFICIAL SEAL
      </text>
      <g transform="rotate(-15 60 60)">
        <rect x="20" y="47" width="80" height="26" rx="3" fill="#ffffff" fillOpacity="0.9" stroke="#2563eb" strokeWidth="1.2" />
        <text x="60" y="61" fill="#2563eb" fontSize="8.5" fontWeight="bold" textAnchor="middle">
          {displayCompanyName.slice(0, 15).toUpperCase()}
        </text>
        <text x="60" y="70" fill="#2563eb" fontSize="6.5" textAnchor="middle">
          VERIFIED
        </text>
      </g>
    </svg>
  );
};

const InvoicePaymentSummaryCard = ({
  baseAmountPaid = 0,
  additionalPayment,
  setAdditionalPayment,
  paymentMethod = "",
  setPaymentMethod,
  effectiveAmountPaid,
  balanceDue,
  grandTotal = 0,
}) => {
  const hasBalance = balanceDue > 0;
  const remainingBeforeCurrent = Math.max(0, grandTotal - (Number(baseAmountPaid) || 0));
  const isPaidInFull = remainingBeforeCurrent > 0 && additionalPayment === remainingBeforeCurrent;

  const handlePayFull = () => {
    if (remainingBeforeCurrent > 0) {
      setAdditionalPayment(remainingBeforeCurrent);
    }
  };

  const handleClear = () => {
    setAdditionalPayment(0);
  };

  return (
    <div className="invoice-control-card" style={{ padding: "14px 16px" }}>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#0f172a" }}>
          Payment & Settlement
        </span>
      </div>

      {/* Input Row with Inside Click-to-Pay Icon */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 6 }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.02em" }}>
            Amount Paid Now
          </span>
        </div>

        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <span
            style={{
              position: "absolute",
              left: 10,
              fontSize: "0.85rem",
              color: "#94a3b8",
              fontWeight: 600,
              pointerEvents: "none",
            }}
          >
            ₹
          </span>
          <input
            type="number"
            min={0}
            step="any"
            className="invoice-field-input"
            placeholder="0"
            value={additionalPayment === 0 ? "" : additionalPayment}
            onChange={(e) => setAdditionalPayment(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))}
            style={{
              paddingLeft: 24,
              paddingRight: remainingBeforeCurrent > 0 ? 34 : 10,
              fontWeight: 600,
              fontSize: "0.9rem",
              color: "#0f172a",
              height: 36,
            }}
          />
          {remainingBeforeCurrent > 0 && (
            <Tooltip title={isPaidInFull ? "Full balance applied (click to reset)" : `Click to pay pending amount (₹${remainingBeforeCurrent.toLocaleString()})`}>
              <button
                type="button"
                onClick={isPaidInFull ? handleClear : handlePayFull}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  padding: 2,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isPaidInFull ? "#16a34a" : "#2563eb",
                  transition: "all 0.15s ease",
                }}
                aria-label="Pay pending balance"
              >
                <CheckmarkCircle02Icon size={18} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Payment Method Selector */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 6 }}>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.02em" }}>
            Payment Method
          </span>
        </div>
        <Select
          allowClear
          placeholder="Select payment method (UPI, Cheque, Bank Transfer...)"
          value={paymentMethod || undefined}
          onChange={(val) => setPaymentMethod && setPaymentMethod(val || "")}
          style={{ width: "100%", height: 36 }}
          options={[
            { value: "UPI", label: "UPI" },
            { value: "Bank Transfer", label: "Bank Transfer / NEFT / IMPS" },
            { value: "Net Banking", label: "Net Banking" },
            { value: "Cheque", label: "Cheque" },
            { value: "Cash", label: "Cash" },
            { value: "Card", label: "Debit / Credit Card" },
          ]}
        />
      </div>

      {/* Structured Summary Strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: baseAmountPaid > 0 ? "repeat(3, 1fr)" : "1fr 1fr",
          gap: 8,
          padding: "10px 12px",
          background: "#f8fafc",
          borderRadius: 8,
          border: "1px solid #f1f5f9",
        }}
      >
        {baseAmountPaid > 0 && (
          <div>
            <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.02em" }}>
              Paid Before
            </div>
            <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#334155", marginTop: 2 }}>
              ₹{baseAmountPaid.toLocaleString()}
            </div>
          </div>
        )}

        <div>
          <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.02em" }}>
            Total Paid
          </div>
          <div style={{ fontSize: "0.92rem", fontWeight: 600, color: effectiveAmountPaid > 0 ? "#16a34a" : "#64748b", marginTop: 2 }}>
            ₹{effectiveAmountPaid.toLocaleString()}
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "0.68rem", color: "#64748b", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.02em" }}>
            Total Balance Due
          </div>
          <div style={{ fontSize: "0.95rem", fontWeight: 700, color: hasBalance ? "#e11d48" : "#16a34a", marginTop: 2 }}>
            ₹{balanceDue.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
};

function InvoiceModal({ visible, open, onClose, onSaveInvoice, lead, prefillLead, prefillClient, record, savedInvoiceRecord, companyCode, employeeId, autoOpenMail = false }) {
  const isModalOpen = Boolean(visible || open);
  const activeLead = lead || prefillLead || prefillClient;
  const activeRecord = record || savedInvoiceRecord;

  // Redux Auth State
  const userInfo = useSelector((state) => state.session?.settings?.userInfo);
  const companyInfo = useSelector((state) => state.executor?.companyInfo);

  const activeCompanyCode = companyCode || companyInfo?.companyCode || userInfo?.companyCode || "";
  const activeEmployeeId = employeeId || userInfo?.id || "";
  const activeEmployeeName = userInfo?.name || "";

  // DB Settings
  const [dbProducts, setDbProducts] = useState([]);
  const [dbTerms, setDbTerms] = useState([]);
  const [dbBankDetails, setDbBankDetails] = useState(null);
  const [dbBankDetails2, setDbBankDetails2] = useState(null);
  const [dbContactDetails, setDbContactDetails] = useState(null);
  const [dbGstPercentage, setDbGstPercentage] = useState(18);
  const [dbGstNumber, setDbGstNumber] = useState("");
  const [dbStateName, setDbStateName] = useState("");
  const [dbStateCode, setDbStateCode] = useState("");
  const [dbCompanyLogo, setDbCompanyLogo] = useState("");
  const [dbCompanySeal, setDbCompanySeal] = useState("");
  const [dbCompanyName, setDbCompanyName] = useState("");
  const [dbRegisteredAddress, setDbRegisteredAddress] = useState("");
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [sealLoadError, setSealLoadError] = useState(false);

  // Form States
  const [documentCompanyCode, setDocumentCompanyCode] = useState(activeCompanyCode);
  const [invoiceNumberStr, setInvoiceNumberStr] = useState("");
  const [invoiceIssuedAt, setInvoiceIssuedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [invoiceDueDate, setInvoiceDueDate] = useState(dayjs().add(15, "day").format("YYYY-MM-DD"));
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemCustomName, setItemCustomName] = useState("");
  const [itemPrice, setItemPrice] = useState(0);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemSacHsn, setItemSacHsn] = useState("998313");
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [selectedCascaderValue, setSelectedCascaderValue] = useState([]);
  const [baseAmountPaid, setBaseAmountPaid] = useState(0);
  const [additionalPayment, setAdditionalPayment] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");

  // Client Details form state
  const [clientCompanyName, setClientCompanyName] = useState("");
  const [clientContactName, setClientContactName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientGstNumber, setClientGstNumber] = useState("");
  const [clientStateName, setClientStateName] = useState("Tamil Nadu");
  const [clientStateCode, setClientStateCode] = useState("33");
  const [isSavingGst, setIsSavingGst] = useState(false);

  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailPrefillData, setMailPrefillData] = useState(null);
  const initialOpenRef = useRef(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("modal-open");
      return () => {
        document.body.classList.remove("modal-open");
      };
    }
  }, [isModalOpen]);

  // Generate QR Code for public invoice view
  useEffect(() => {
    const publicToken = activeRecord?.publicToken || invoiceNumberStr || "INV_PREVIEW";
    const publicLink = `${FRONTEND_BASE_URL}/invoice/${encodeURIComponent(publicToken)}`;
    QRCode.toDataURL(publicLink, {
      width: 140,
      margin: 1,
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((url) => setQrDataUrl(url))
      .catch((err) => console.error("QR generation error:", err));
  }, [activeRecord, invoiceNumberStr]);

  // Load DB Settings
  const loadDbSettings = useCallback(async (compCode) => {
    if (!compCode) return;
    try {
      const res = await getCompanySettings(compCode);
      if (res?.success && res.settings) {
        applyDbSettings(res.settings, {
          setDbProducts,
          setDbTerms,
          setDbBankDetails,
          setDbBankDetails2,
          setDbContactDetails,
          setDbGstPercentage,
          setDbGstNumber,
          setDbStateName,
          setDbStateCode,
          setDbCompanyLogo,
          setLogoLoadError,
          setDbCompanySeal,
          setSealLoadError,
          setDbCompanyName,
          setDbRegisteredAddress,
        });
      }
    } catch (err) {
      console.error("[InvoiceModal] loadDbSettings error:", err);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      loadDbSettings(documentCompanyCode);
    }
  }, [isModalOpen, documentCompanyCode, loadDbSettings]);

  // Initialize or Reset Modal Data on modal open or record change only
  useEffect(() => {
    if (isModalOpen && !initialOpenRef.current) {
      initialOpenRef.current = true;
      setEditingItemIndex(null);
      setSelectedCascaderValue([]);
      const commonSetters = {
        setInvoiceNumberStr,
        setInvoiceIssuedAt,
        setInvoiceDueDate,
        setInvoiceItems,
        setBaseAmountPaid,
        setAdditionalPayment,
        setPaymentMethod,
        setDocumentCompanyCode,
        setClientCompanyName,
        setClientContactName,
        setClientPhone,
        setClientEmail,
        setClientAddress,
        setClientGstNumber,
        setClientStateName,
        setClientStateCode,
        setSelectedProduct,
        setItemPrice,
        setItemSacHsn,
        setItemQuantity,
      };

      if (activeRecord) {
        initRecordData(activeRecord, activeCompanyCode, commonSetters);
      } else {
        initNewLeadData(activeLead, activeCompanyCode, dbProducts, commonSetters);
      }
    } else if (!isModalOpen) {
      initialOpenRef.current = false;
      setEditingItemIndex(null);
      setSelectedCascaderValue([]);
    }
  }, [isModalOpen, activeRecord, activeLead, activeCompanyCode, dbProducts]);

  const hasCustomerGst = checkHasCustomerGst(clientGstNumber);

  // Auto-detect inter-state (IGST) vs intra-state (CGST + SGST) based on Seller and Buyer states
  const effectiveIsInterState = useMemo(() => {
    if (activeRecord?.isInterState !== undefined && activeRecord?.isInterState !== null) {
      return Boolean(activeRecord.isInterState);
    }
    return isInterStateSupply(dbStateCode, clientStateCode, dbStateName, clientStateName);
  }, [activeRecord, dbStateCode, clientStateCode, dbStateName, clientStateName]);

  // Standard GST Rate taken directly from company settings
  const effectiveGstRate = Number(dbGstPercentage) || 18;

  // Effective Bank Details: Primary for GST clients, Secondary for Non-GST clients
  const effectiveBankDetails = useMemo(() => {
    return resolveEffectiveBankDetails(activeRecord, hasCustomerGst, dbBankDetails, dbBankDetails2);
  }, [activeRecord, hasCustomerGst, dbBankDetails, dbBankDetails2]);

  // Tax and Totals Calculations
  const calculations = useMemo(() => {
    return calculateInvoiceTotals(invoiceItems, effectiveGstRate, effectiveIsInterState);
  }, [invoiceItems, effectiveGstRate, effectiveIsInterState]);

  const effectiveAmountPaid = (Number(baseAmountPaid) || 0) + (Number(additionalPayment) || 0);
  const balanceDue = Math.max(0, calculations.grandTotal - effectiveAmountPaid);
  const paymentStatus = useMemo(() => {
    if (calculations.grandTotal > 0 && effectiveAmountPaid >= calculations.grandTotal) {
      return "paid";
    }
    if (effectiveAmountPaid > 0 && calculations.grandTotal > 0) {
      return "partially paid";
    }
    return "unpaid";
  }, [calculations.grandTotal, effectiveAmountPaid]);

  // Grouped HSN/SAC GST Breakdown for the official tax breakdown table
  const gstBreakdown = useMemo(() => {
    return calculateGstBreakdown(invoiceItems, calculations, effectiveGstRate, effectiveIsInterState);
  }, [invoiceItems, calculations, effectiveGstRate, effectiveIsInterState]);

  // Product Cascader Options
  const productCascaderOptions = useMemo(() => buildProductCascaderOptions(dbProducts), [dbProducts]);

  // Handlers
  const handleProductCascaderSelect = (value, selectedOptions) => {
    setSelectedCascaderValue(value || []);
    if (!value || value.length === 0) {
      setSelectedProduct(null);
      setItemCustomName("");
      setItemPrice(0);
      setItemSacHsn("998313");
      return;
    }
    const resolved = resolveCascaderProduct(productCascaderOptions, value, selectedOptions);
    if (resolved) {
      setSelectedProduct(resolved);
      setItemCustomName(resolved.displayName || resolved.name || "");
      setItemPrice(resolved.price || 0);
      setItemSacHsn(resolved.sacHsn || "998313");
    }
  };

  const handleEditLineItem = (index) => {
    const item = invoiceItems[index];
    if (!item) return;
    setEditingItemIndex(index);
    setItemCustomName(item.name || "");
    setItemSacHsn(item.sacHsn || "998313");
    setItemQuantity(item.quantity || 1);
    setItemPrice(item.rate !== undefined ? item.rate : item.price || 0);
    setSelectedProduct(null);
    setSelectedCascaderValue([]);
  };

  const handleCancelEditLineItem = () => {
    setEditingItemIndex(null);
    setItemCustomName("");
    setSelectedProduct(null);
    setSelectedCascaderValue([]);
    setItemPrice(0);
    setItemQuantity(1);
    setItemSacHsn("998313");
  };

  const handleAddLineItem = () => {
    const name = itemCustomName.trim() || selectedProduct?.name || "Service Item";
    const rate = Number(itemPrice) || 0;
    const qty = Number(itemQuantity) || 1;
    const sacHsn = itemSacHsn || "998313";
    const taxable = rate * qty;

    if (!name.trim()) {
      message.warning("Please enter an item name.");
      return;
    }
    if (rate <= 0) {
      message.warning("Please enter a valid rate greater than 0.");
      return;
    }

    const itemObj = {
      name,
      sacHsn,
      quantity: qty,
      rate,
      taxable,
      total: taxable,
    };

    if (editingItemIndex !== null && editingItemIndex >= 0 && editingItemIndex < invoiceItems.length) {
      const updated = [...invoiceItems];
      updated[editingItemIndex] = itemObj;
      setInvoiceItems(updated);
      setEditingItemIndex(null);
      message.success("Item updated successfully.");
    } else {
      setInvoiceItems([...invoiceItems, itemObj]);
    }

    setItemCustomName("");
    setSelectedProduct(null);
    setSelectedCascaderValue([]);
    setItemPrice(0);
    setItemQuantity(1);
    setItemSacHsn("998313");
  };

  const handleRemoveLineItem = (index) => {
    if (editingItemIndex === index) {
      handleCancelEditLineItem();
    } else if (editingItemIndex !== null && index < editingItemIndex) {
      setEditingItemIndex(editingItemIndex - 1);
    }
    setInvoiceItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveCompanyGst = async () => {
    if (!clientCompanyName && !activeLead?._id && !activeRecord?.clientId) {
      message.warning("Customer company name is required to save GST number.");
      return;
    }
    setIsSavingGst(true);
    try {
      const res = await saveCompanyGst(documentCompanyCode, {
        companyName: clientCompanyName,
        leadId: activeLead?._id || activeLead?.id || activeRecord?.leadId,
        clientId: activeRecord?.clientId || activeLead?.clientId,
        gstNumber: (clientGstNumber || "").trim().toUpperCase(),
      });

      if (res?.success !== false) {
        message.success(clientGstNumber && clientGstNumber.trim() ? `GST Number (${clientGstNumber.trim()}) saved for ${clientCompanyName || "customer"}!` : `GST Number cleared for ${clientCompanyName || "customer"}.`);
      } else {
        message.error(res?.message || "Failed to save GST number.");
      }
    } catch (err) {
      console.error("[InvoiceModal] handleSaveCompanyGst error:", err);
      message.error("Failed to save GST number.");
    } finally {
      setIsSavingGst(false);
    }
  };

  const prepareAndOpenMailComposer = async (savedInvNumber) => {
    const customerCompanyName = clientCompanyName || activeLead?.leadCompanyName || activeLead?.companyName || activeLead?.contactName || "Customer";
    const cleanCustomerName = customerCompanyName
      .replace(/[^a-zA-Z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
    const finalDocNumber = savedInvNumber || invoiceNumberStr || "INV2026";
    const fileName = `${cleanCustomerName}_${finalDocNumber}.pdf`;

    const previewEl = document.querySelector("#invoice-preview") || document.querySelector(".invoice-document-scroll");

    let invoiceFile = null;
    if (previewEl) {
      try {
        message.loading({ content: "Preparing PDF attachment...", key: "invoice-pdf", duration: 2 });
        invoiceFile = await generatePdfFromElement(previewEl, fileName);
        message.destroy("invoice-pdf");
      } catch (err) {
        console.error("PDF generation failed:", err);
      }
    }

    const emailSubject = `Invoice ${finalDocNumber} - ${customerCompanyName}`;
    const emailBody = `<p>Dear ${clientContactName || "Sir/Madam"},</p>
<p>Please find attached your invoice <strong>${finalDocNumber}</strong> from <strong>${displayCompanyName}</strong> for <strong>${customerCompanyName}</strong>.</p>
<p><strong>Invoice Details:</strong></p>
<ul>
  <li><strong>Invoice Number:</strong> ${finalDocNumber}</li>
  <li><strong>Invoice Date:</strong> ${invoiceIssuedAt}</li>
  <li><strong>Due Date:</strong> ${invoiceDueDate || "Immediate"}</li>
  <li><strong>Grand Total:</strong> ₹${Number(calculations.grandTotal || 0).toLocaleString("en-IN")}</li>
  <li><strong>Balance Due:</strong> ₹${Number(balanceDue || 0).toLocaleString("en-IN")}</li>
</ul>
<p>Kindly review the attached invoice PDF file. Please let us know once the payment is completed.</p>
<br/>
<p>Warm regards,<br/><strong>${activeEmployeeName || "Accounts Team"}</strong><br/>${displayCompanyName}</p>`;

    setMailPrefillData({
      to: clientEmail || activeLead?.directorEmailAddress || activeLead?.email || "",
      subject: emailSubject,
      html: emailBody,
      attachments: invoiceFile ? [invoiceFile] : [],
    });
    setMailDrawerOpen(true);
  };

  const handlePrint = async (action = "print") => {
    if (action === "email") {
      if (!activeRecord?._id) {
        await handleSaveInvoice();
      } else {
        await prepareAndOpenMailComposer(invoiceNumberStr);
      }
      return;
    }

    const previewEl = document.querySelector("#invoice-preview");
    if (!previewEl) {
      window.print();
      return;
    }

    const styles = Array.from(document.querySelectorAll("style, link[rel='stylesheet']"))
      .map((s) => s.outerHTML)
      .join("\n");

    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "none";
    printFrame.style.zIndex = "-1";
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice - ${invoiceNumberStr || "INV"}</title>
  ${styles}
  <style>
    @page {
      size: A4 portrait;
      margin: 4mm;
    }
    *, *::before, *::after {
      box-sizing: border-box !important;
    }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      width: 100% !important;
      height: 100% !important;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    #invoice-preview {
      width: 100% !important;
      max-width: 202mm !important;
      height: 289mm !important;
      min-height: 289mm !important;
      max-height: 289mm !important;
      margin: 0 auto !important;
      box-shadow: none !important;
      border: 1.5px solid #8fa1bb !important;
      box-sizing: border-box !important;
      page-break-after: avoid !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      overflow: hidden !important;
    }
    .invoice-main-table-wrap { flex: 1 1 auto !important; display: flex !important; flex-direction: column !important; }
    .invoice-main-table { flex: 1 1 auto !important; height: 100% !important; }
    .invoice-footer-stack { margin-top: auto !important; flex-shrink: 0 !important; }
    .tax-invoice-bar { padding: 4px 10px !important; }
    .sender-info-block { padding: 6px 10px !important; min-height: auto !important; gap: 10px !important; }
    .sender-logo-wrap, .sender-text-logo { flex: 0 0 54px !important; height: 54px !important; font-size: 14px !important; }
    .company-title { font-size: 12px !important; margin-bottom: 2px !important; }
    .company-gst, .company-addr, .company-contact { font-size: 9.5px !important; line-height: 1.25 !important; margin-bottom: 2px !important; }
    .client-info-block { padding: 6px 10px !important; }
    .block-kicker, h3.block-kicker { font-size: 9.5px !important; margin-bottom: 4px !important; font-weight: 600 !important; color: #4b5563 !important; }
    .client-company-title { font-size: 11.5px !important; margin-bottom: 4px !important; font-weight: 600 !important; }
    .client-billing-address { font-size: 9.5px !important; line-height: 1.25 !important; }
    .client-billing-address p { margin: 1px 0 2px !important; }
    .meta-field-cell { padding: 4px 8px !important; min-height: auto !important; }
    .field-label { font-size: 8.5px !important; }
    .field-val { font-size: 10px !important; }
    .meta-field-full-row { padding: 4px 8px !important; min-height: auto !important; }
    .invoice-qr-label { margin-bottom: 3px !important; font-size: 8.5px !important; }
    .invoice-qr-image, .invoice-qr-placeholder-box { width: 52px !important; height: 52px !important; }
    .meta-status-space { padding: 4px 8px !important; min-height: auto !important; }
    .invoice-status-value { font-size: 10.5px !important; }
    .invoice-main-table th, .invoice-main-table td { padding: 4px 6px !important; font-size: 9.5px !important; }
    .invoice-main-table th { font-size: 9px !important; }
    .summary-left-cell { padding: 5px 8px !important; font-size: 10px !important; }
    .summary-line { padding: 3px 6px !important; font-size: 9.5px !important; }
    .grand-total-line { padding: 3px 6px !important; font-size: 11px !important; font-weight: 600 !important; }
    .grand-total-val { font-size: 12px !important; font-weight: 600 !important; }
    .total-in-words-row { padding: 4px 8px !important; font-size: 9.5px !important; }
    .gst-breakdown-table th, .gst-breakdown-table td { padding: 3px 5px !important; font-size: 8.5px !important; }
    .footer-col-bank { padding: 6px 8px !important; }
    .footer-kicker, .notes-kicker, .terms-kicker, h3.footer-kicker, h3.notes-kicker, h3.terms-kicker { font-size: 9px !important; margin-bottom: 3px !important; font-weight: 600 !important; color: #4b5563 !important; }
    .bank-details-list { font-size: 9.5px !important; grid-template-columns: 65px minmax(0, 1fr) !important; gap: 2px !important; }
    .footer-col-sig { padding: 6px 8px !important; display: flex !important; flex-direction: column !important; align-items: center !important; justify-content: space-between !important; text-align: center !important; }
    .sig-header { font-size: 8.5px !important; line-height: 1.25 !important; display: block !important; width: 100% !important; text-align: center !important; word-break: break-word !important; margin-bottom: 2px !important; font-weight: 600 !important; }
    .sig-stamp-container { height: 48px !important; min-height: 48px !important; width: 100% !important; display: flex !important; align-items: center !important; justify-content: center !important; margin: 2px 0 !important; overflow: hidden !important; }
    .signature-stamp-img { max-width: 65px !important; max-height: 48px !important; width: auto !important; height: auto !important; object-fit: contain !important; display: block !important; }
    .sig-footer { font-size: 8.5px !important; display: block !important; width: 100% !important; text-align: center !important; margin-top: 2px !important; }
    .notes-block { padding: 5px 8px !important; }
    .notes-block p { font-size: 9px !important; line-height: 1.25 !important; }
    .terms-block { padding: 5px 8px !important; }
    .terms-block ol { font-size: 8px !important; line-height: 1.2 !important; padding-left: 12px !important; }
    .terms-block li { margin-bottom: 1px !important; }
    .invoice-bottom-page-footer { padding: 3px 8px !important; font-size: 8px !important; }
  </style>
</head>
<body>
  ${previewEl.outerHTML}
</body>
</html>`);
    frameDoc.close();

    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      setTimeout(() => {
        try {
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
        } catch (e) {
          // ignore
        }
      }, 3000);
    }, 250);
  };

  const handleSaveInvoice = async () => {
    if (invoiceItems.length === 0) {
      message.warning("Please add at least one line item.");
      return;
    }

    setIsSaving(true);
    try {
      const formattedItems = invoiceItems.map((it) => {
        const taxable = Number(it.quantity || 1) * Number(it.rate || 0);
        const itemCgst = effectiveIsInterState ? 0 : (taxable * (calculations.halfRate / 100));
        const itemSgst = effectiveIsInterState ? 0 : (taxable * (calculations.halfRate / 100));
        const itemIgst = effectiveIsInterState ? (taxable * (effectiveGstRate / 100)) : 0;
        const total = taxable + (effectiveIsInterState ? itemIgst : (itemCgst + itemSgst));

        return {
          name: it.name || "Item",
          sacHsn: it.sacHsn || "998313",
          quantity: Number(it.quantity || 1),
          rate: Number(it.rate || 0),
          taxable,
          cgst: itemCgst,
          sgst: itemSgst,
          igst: itemIgst,
          total,
        };
      });

      const payload = {
        invoiceNumber: invoiceNumberStr,
        companyCode: documentCompanyCode,
        leadId: activeLead?._id || activeLead?.id || activeLead?.leadId || undefined,
        dealId: activeLead?.dealId || activeLead?.deal?._id || undefined,
        leadCode: activeLead?.leadCode || activeLead?.code || "",
        leadCompanyName: clientCompanyName || "Client Enterprise",
        contactName: clientContactName || "Contact Person",
        contactNumber: clientPhone || "",
        directorEmailAddress: clientEmail || "",
        invoiceDate: new Date(invoiceIssuedAt).toISOString(),
        dueDate: invoiceDueDate ? new Date(invoiceDueDate).toISOString() : null,
        items: formattedItems,
        subtotal: calculations.subtotal,
        gstPercentage: effectiveGstRate,
        isInterState: effectiveIsInterState,
        cgst: calculations.cgstAmount,
        sgst: calculations.sgstAmount,
        igst: calculations.igstAmount,
        gstAmount: calculations.totalGst,
        total: calculations.grandTotal,
        amountPaid: effectiveAmountPaid,
        balanceDue: balanceDue,
        paymentStatus: paymentStatus,
        paymentMethod: paymentMethod || "",
        employeeId: activeEmployeeId || undefined,
        employeeName: activeEmployeeName || undefined,
        companySnapshot: {
          name: dbCompanyName || "Softrate Technologies Private Limited",
          logo: dbCompanyLogo,
          seal: dbCompanySeal,
          gstNumber: dbGstNumber,
          stateName: dbStateName,
          stateCode: dbStateCode,
          state: dbStateName,
          registeredAddress: dbRegisteredAddress,
          terms: dbTerms.join("\n"),
          bankDetails: effectiveBankDetails,
        },
        clientSnapshot: {
          companyName: clientCompanyName,
          contactName: clientContactName,
          phone: clientPhone,
          email: clientEmail,
          address: clientAddress,
          gstNumber: clientGstNumber,
          stateName: clientStateName,
          stateCode: clientStateCode,
          state: clientStateName,
        },
      };

      let result;
      if (activeRecord?._id) {
        result = await updateInvoice(documentCompanyCode, activeRecord._id, payload);
      } else {
        result = await createInvoice(documentCompanyCode, payload);
      }

      if (result?.success !== false) {
        const savedInvNum = result?.invoice?.invoiceNumber || invoiceNumberStr;
        setInvoiceNumberStr(savedInvNum);
        message.success(`Invoice ${savedInvNum} saved successfully!`);
        if (onSaveInvoice) onSaveInvoice(result?.invoice || payload);
        prepareAndOpenMailComposer(savedInvNum);
      } else {
        message.error(result?.message || "Failed to save invoice.");
      }
    } catch (err) {
      console.error("[InvoiceModal] save error:", err);
      message.error("An error occurred while saving invoice.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isModalOpen) return null;

  const displayCompanyName = dbCompanyName || "Softrate Technologies Private Limited";

  return (
    <div className="invoice-modal-overlay">
      <div className="invoice-modal-container">
        {/* Modal Top Header */}
        <div className="invoice-modal-header">
          <div>
            <div className="invoice-section-kicker">{activeRecord ? "Tax Invoice Manager & Editor" : "Tax Invoice Generator"}</div>
            <div style={{ margin: 0, fontSize: "1.1rem", color: "#0f172a", fontWeight: 600 }}>{activeRecord ? `Edit & Manage Invoice #${invoiceNumberStr}` : "Create & Issue New Tax Invoice"}</div>
          </div>
          <Button type="text" shape="circle" icon={<CloseOutlined />} onClick={onClose} />
        </div>

        {/* 2-Column Split: Controls + Live A4 Preview */}
        <div className="invoice-builder-layout">
          {/* LEFT: Builder & Edit Form Controls */}
          <div className="invoice-builder-controls">
            {/* Customer & GST Details Section */}
            <div className="invoice-control-card">
              <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a", textTransform: "uppercase", marginBottom: 10 }}>Customer & GST Details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <label className="invoice-mini-field">
                  <span>Customer Company</span>
                  <input type="text" className="invoice-field-input" value={clientCompanyName} onChange={(e) => setClientCompanyName(e.target.value)} placeholder="e.g. Acme Corp" />
                </label>
                <label className="invoice-mini-field">
                  <span>Contact Person</span>
                  <input type="text" className="invoice-field-input" value={clientContactName} onChange={(e) => setClientContactName(e.target.value)} placeholder="Contact Name" />
                </label>
              </div>

              {/* Customer GST Number with inline Save Button */}
              <div style={{ marginBottom: 10 }}>
                <label className="invoice-mini-field">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span>Customer GSTIN</span>
                    {hasCustomerGst ? (
                      <span style={{ fontSize: "0.72rem", color: "#16a34a", fontWeight: 600 }}>✓ GSTIN Verified</span>
                    ) : (
                      <span style={{ fontSize: "0.72rem", color: "#64748b" }}>(Unregistered / B2C)</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      type="text"
                      className="invoice-field-input"
                      placeholder="e.g. 33AABCU9603R1ZM"
                      value={clientGstNumber}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().trim();
                        setClientGstNumber(val);
                        if (val.length >= 2) {
                          const derived = getStateInfoFromGstin(val);
                          if (derived.stateCode) {
                            setClientStateCode(derived.stateCode);
                            if (derived.stateName) {
                              setClientStateName(derived.stateName);
                            }
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        textTransform: "uppercase",
                        fontWeight: 600,
                        letterSpacing: "0.03em",
                      }}
                    />
                    <Button
                      type="primary"
                      icon={<SaveOutlined style={{ color: "#ffffff" }} />}
                      loading={isSavingGst}
                      onClick={handleSaveCompanyGst}
                      style={{
                        backgroundColor: "#2563eb",
                        borderColor: "#2563eb",
                        color: "#ffffff",
                        borderRadius: 6,
                        fontWeight: 600,
                        fontSize: "0.82rem",
                        height: 36,
                        flexShrink: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span style={{ color: "#ffffff", fontWeight: 600 }}>Save</span>
                    </Button>
                  </div>
                </label>
              </div>


              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label className="invoice-mini-field">
                  <span>Phone Number</span>
                  <input type="text" className="invoice-field-input" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="Contact Phone" />
                </label>
                <label className="invoice-mini-field">
                  <span>Email Address</span>
                  <input type="email" className="invoice-field-input" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="Contact Email" />
                </label>
              </div>
            </div>


            {/* Document Meta Dates Information */}
            <div className="invoice-control-card">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label className="invoice-mini-field">
                  <span>Invoice Date</span>
                  <DatePicker value={invoiceIssuedAt ? dayjs(invoiceIssuedAt) : null} onChange={(d) => setInvoiceIssuedAt(d ? d.format("YYYY-MM-DD") : "")} style={{ width: "100%", height: 36 }} />
                </label>
                <label className="invoice-mini-field">
                  <span>Due Date</span>
                  <DatePicker value={invoiceDueDate ? dayjs(invoiceDueDate) : null} onChange={(d) => setInvoiceDueDate(d ? d.format("YYYY-MM-DD") : "")} style={{ width: "100%", height: 36 }} />
                </label>
              </div>
            </div>

            {/* Unified Line Items Section (Add / Edit Form + Items List) */}
            <div className="invoice-control-card" style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.025em" }}>Line Items</span>
                  <span style={{ fontSize: "0.7rem", fontWeight: 600, background: "#f1f5f9", color: "#475569", padding: "1px 6px", borderRadius: 10 }}>{invoiceItems.length}</span>
                  {editingItemIndex !== null && <span style={{ fontSize: "0.68rem", fontWeight: 700, background: "#dbeafe", color: "#1d4ed8", padding: "1px 6px", borderRadius: 4, textTransform: "uppercase" }}>Editing #{editingItemIndex + 1}</span>}
                </div>
                {editingItemIndex !== null && (
                  <Button size="small" type="link" onClick={handleCancelEditLineItem} style={{ fontSize: "0.75rem", padding: 0, height: "auto", color: "#64748b" }}>
                    Cancel Edit
                  </Button>
                )}
              </div>
              {dbProducts.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <Cascader placeholder="Select Product / Sub-Product from Catalog..." options={productCascaderOptions} value={selectedCascaderValue} onChange={handleProductCascaderSelect} changeOnSelect showSearch={{ filter: cascaderSearchFilter }} allowClear style={{ width: "100%", height: 36 }} />
                </div>
              )}
              <div className="invoice-form-grid">
                <div className="invoice-mini-field">
                  <span>Item Name</span>
                  <input type="text" className="invoice-field-input" value={itemCustomName} onChange={(e) => setItemCustomName(e.target.value)} placeholder="e.g. Enterprise Software" />
                </div>
                <div className="invoice-mini-field">
                  <span>HSN/SAC</span>
                  <input type="text" className="invoice-field-input" value={itemSacHsn} onChange={(e) => setItemSacHsn(e.target.value)} placeholder="998313" />
                </div>
                <div className="invoice-mini-field">
                  <span>Qty</span>
                  <input type="number" min={1} className="invoice-field-input" value={itemQuantity} onChange={(e) => setItemQuantity(Math.max(1, Number(e.target.value)))} />
                </div>
                <div className="invoice-mini-field">
                  <span>Rate (₹)</span>
                  <input type="number" min={0} step="any" className="invoice-field-input" value={itemPrice || ""} onChange={(e) => setItemPrice(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))} placeholder="0" />
                </div>
              </div>
              {editingItemIndex !== null ? (
                <div style={{ display: "flex", gap: 8, marginTop: 10, marginBottom: 14 }}>
                  <Button type="primary" icon={<CheckOutlined />} onClick={handleAddLineItem} style={{ flex: 1, background: "#2563eb", borderColor: "#2563eb", fontWeight: 600 }}>
                    Update Item
                  </Button>
                  <Button onClick={handleCancelEditLineItem} style={{ width: 80 }}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddLineItem} style={{ width: "100%", marginTop: 10, marginBottom: 14, borderColor: "#2563eb", color: "#2563eb", fontWeight: 500 }}>
                  Add Item to Invoice
                </Button>
              )}

              {/* Items List directly embedded inside this card */}
              <div style={{ borderTop: "1px solid #f1f5f9", margin: "0 -16px -14px -16px", background: "#fafafa" }}>
                <div style={{ padding: "8px 16px 4px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.025em" }}>Added Items ({invoiceItems.length})</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", textAlign: "left", color: "#475569", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ padding: "6px 12px", fontSize: "0.72rem" }}>Description</th>
                      <th style={{ padding: "6px 6px", textAlign: "center", fontSize: "0.72rem" }}>Qty</th>
                      <th style={{ padding: "6px 8px", textAlign: "right", fontSize: "0.72rem" }}>Rate</th>
                      <th style={{ padding: "6px 8px", textAlign: "right", fontSize: "0.72rem" }}>Total</th>
                      <th style={{ padding: "6px 8px", width: 70, textAlign: "center", fontSize: "0.72rem" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((it, idx) => (
                      <tr
                        key={it.id || it._id || `${it.name || "item"}-${idx}`}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: editingItemIndex === idx ? "#eff6ff" : "transparent",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <td style={{ padding: "8px 12px" }}>
                          <div style={{ fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: 4 }}>
                            {it.name}
                            {editingItemIndex === idx && <span style={{ fontSize: "0.65rem", background: "#3b82f6", color: "#fff", padding: "0 4px", borderRadius: 3 }}>Editing</span>}
                          </div>
                          {it.sacHsn && <div style={{ fontSize: "0.7rem", color: "#64748b" }}>HSN: {it.sacHsn}</div>}
                        </td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: "#334155" }}>{it.quantity}</td>
                        <td style={{ padding: "8px 8px", textAlign: "right", color: "#334155" }}>₹{(Number(it.rate) || 0).toLocaleString()}</td>
                        <td style={{ padding: "8px 8px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>₹{(Number(it.total) || 0).toLocaleString()}</td>
                        <td style={{ padding: "8px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                          <Button type="text" size="small" icon={<EditOutlined style={{ color: "#2563eb" }} />} onClick={() => handleEditLineItem(idx)} title="Edit this item" style={{ marginRight: 2, padding: "0 4px" }} />
                          <Button type="text" size="small" icon={<DeleteOutlined style={{ color: "#ef4444" }} />} onClick={() => handleRemoveLineItem(idx)} title="Delete this item" style={{ padding: "0 4px" }} />
                        </td>
                      </tr>
                    ))}
                    {invoiceItems.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: "14px", textAlign: "center", color: "#94a3b8", fontSize: "0.78rem" }}>
                          No line items added yet. Fill fields above and click Add.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Details Section & Partial Amount Entry */}
            <InvoicePaymentSummaryCard
              baseAmountPaid={baseAmountPaid}
              additionalPayment={additionalPayment}
              setAdditionalPayment={setAdditionalPayment}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              effectiveAmountPaid={effectiveAmountPaid}
              balanceDue={balanceDue}
              grandTotal={calculations.grandTotal}
            />

            {/* Invoice Remarks / Notes */}
            <div className="invoice-control-card">
              <label className="invoice-mini-field">
                <span>Invoice Notes / Remarks</span>
                <textarea rows={2} className="invoice-field-textarea" placeholder="Custom notes or payment instructions..." value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} />
              </label>
            </div>
          </div>

          {/* RIGHT: EXACT SOFTRATE WORKSPACE TAX INVOICE PREVIEW (#invoice-preview) */}
          <div className="invoice-builder-preview">
            <div id="invoice-preview" className="invoice-preview">
              {/* 1. Tax Invoice Bar */}
              <div className="tax-invoice-bar">
                <span className="invoice-title-main">TAX INVOICE</span>
                <span className="invoice-title-sub">ORIGINAL FOR RECIPIENT</span>
              </div>

              {/* 2. Grid Header */}
              <div className="invoice-grid-header">
                <div className="invoice-grid-col-left">
                  <div className="sender-info-block">
                    <div className="sender-logo-wrap">
                      <InvoiceSenderLogo logoUrl={dbCompanyLogo} logoLoadError={logoLoadError} onLogoError={() => setLogoLoadError(true)} displayCompanyName={displayCompanyName} />
                    </div>
                    <div className="sender-details">
                      <div className="company-title">{displayCompanyName}</div>
                      {dbGstNumber && (
                        <p className="company-gst">
                          GSTIN: <strong>{dbGstNumber}</strong>
                          {(dbStateName || dbStateCode) && (
                            <>
                              {" | "}State: <strong>{dbStateName || "—"}</strong>
                              {dbStateCode && <> (Code: <strong>{dbStateCode}</strong>)</>}
                            </>
                          )}
                        </p>
                      )}
                      {!dbGstNumber && (dbStateName || dbStateCode) && (
                        <p className="company-gst">
                          State: <strong>{dbStateName || "—"}</strong>
                          {dbStateCode && <> (Code: <strong>{dbStateCode}</strong>)</>}
                        </p>
                      )}
                      <p className="company-addr">{dbRegisteredAddress || "No. 60A, Softrate Tech Park, Velleeswaran Kovil Street, Chennai 600 122"}</p>
                      <p className="company-contact">
                        <span>Mobile: {dbContactDetails?.phone || "+91 8148633580"}</span>
                        <span> | </span>
                        <span>Email: {dbContactDetails?.email || "helpdesk@softrateglobal.com"}</span>
                      </p>
                    </div>
                  </div>

                  <div className="client-info-block">
                    <div className="block-kicker">Customer Details:</div>
                    <strong className="client-company-title">{(clientCompanyName || "CLIENT COMPANY").toUpperCase()}</strong>
                    <div className="client-billing-address">
                      <strong>Billing address:</strong>
                      <p>{clientAddress || "—"}</p>
                      {clientPhone && <p className="client-phone">Ph: {clientPhone}</p>}
                      {clientEmail && <p style={{ margin: 0 }}>Email: {clientEmail}</p>}
                      {clientGstNumber && (
                        <p style={{ margin: "2px 0 0" }}>
                          GSTIN: <strong>{clientGstNumber}</strong>
                          {(clientStateName || clientStateCode) && (
                            <>
                              {" | "}State: <strong>{clientStateName || "—"}</strong>
                              {clientStateCode && <> (Code: <strong>{clientStateCode}</strong>)</>}
                            </>
                          )}
                        </p>
                      )}
                      {!clientGstNumber && (clientStateName || clientStateCode) && (
                        <p style={{ margin: "2px 0 0" }}>
                          State: <strong>{clientStateName || "—"}</strong>
                          {clientStateCode && <> (Code: <strong>{clientStateCode}</strong>)</>}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="invoice-grid-col-right">
                  <div className="meta-field-row">
                    <div className="meta-field-cell">
                      <span className="field-label">Invoice #:</span>
                      <strong className="field-val">{invoiceNumberStr}</strong>
                    </div>
                    <div className="meta-field-cell">
                      <span className="field-label">Invoice Date:</span>
                      <strong className="field-val">{dayjs(invoiceIssuedAt).format("DD MMM YYYY")}</strong>
                    </div>
                  </div>
                  <div className="meta-field-full-row">
                    <span className="field-label invoice-qr-label">Scan to View E-Invoice</span>
                    <div className="invoice-qr-wrap">{qrDataUrl ? <img src={qrDataUrl} alt="Scan to View E-Invoice QR Code" className="invoice-qr-image" /> : <div className="invoice-qr-placeholder-box">QR</div>}</div>
                  </div>
                  <div className="meta-filler-space meta-status-space">
                    <div className="invoice-status-line">
                      <span className="field-label">Invoice Status:</span>
                      <strong
                        className={`invoice-status-value ${
                          paymentStatus === "paid"
                            ? "invoice-status-value-paid"
                            : paymentStatus === "partially paid"
                            ? "invoice-status-value-partial"
                            : ""
                        }`}
                      >
                        {paymentStatus.toUpperCase()}
                      </strong>
                    </div>
                    {paymentMethod && (
                      <div className="invoice-status-line" style={{ marginTop: 4 }}>
                        <span className="field-label">Payment Method:</span>
                        <strong style={{ fontSize: "0.82rem", color: "#1e293b" }}>
                          {paymentMethod}
                        </strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 3. Items Table */}
              <div className="invoice-main-table-wrap">
                <table className="invoice-main-table">
                  <thead>
                    <tr>
                      <th className="col-sno text-center">#</th>
                      <th className="col-desc">Item</th>
                      <th className="col-hsn text-center">HSN/SAC</th>
                      <th className="col-rate text-right">Rate/ Item</th>
                      <th className="col-qty text-center">Qty</th>
                      <th className="col-taxable text-right">Taxable Value</th>
                      <th className="col-tax text-right">Tax Amount</th>
                      <th className="col-amount text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((item, i) => {
                      const qty = Number(item.quantity) || 1;
                      const rate = Number(item.rate) || 0;
                      const taxable = qty * rate;
                      const taxAmount = (taxable * (calculations.isExempt ? 0 : effectiveGstRate)) / 100;
                      const total = taxable + taxAmount;

                      return (
                        <tr key={item.id || item._id || `${item.name || "item"}-${i}`} className="item-row">
                          <td className="text-center">{i + 1}</td>
                          <td>
                            <div className="item-main-name">
                              <strong>{item.name || "Service / Product"}</strong>
                            </div>
                          </td>
                          <td className="text-center">{item.sacHsn || "—"}</td>
                          <td className="text-right">₹{rate.toLocaleString()}</td>
                          <td className="text-center">{qty}</td>
                          <td className="text-right">₹{taxable.toLocaleString()}</td>
                          <td className="text-right">
                            {!calculations.isExempt ? (
                              <>
                                ₹{taxAmount.toFixed(2)}
                                <div className="item-tax-pct">({effectiveGstRate}%)</div>
                              </>
                            ) : (
                              <span style={{ fontSize: 10, color: "#64748b" }}>Exempt</span>
                            )}
                          </td>
                          <td className="text-right">
                            <strong>₹{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Table Summary Row */}
                    <tr className="table-summary-row">
                      <td colSpan={5} className="summary-left-cell">
                        Total Items / Qty : {invoiceItems.length} / {invoiceItems.reduce((acc, it) => acc + (Number(it.quantity) || 1), 0)}.000
                      </td>
                      <td colSpan={3} className="summary-right-cell">
                        {paymentStatus !== "unpaid" && (
                          <>
                            <div className="summary-line">
                              <span>Taxable Amount</span>
                              <strong>₹{calculations.subtotal.toLocaleString()}</strong>
                            </div>
                            {effectiveIsInterState ? (
                              <div className="summary-line">
                                <span>IGST ({effectiveGstRate}%)</span>
                                <strong>₹{calculations.igstAmount.toFixed(2)}</strong>
                              </div>
                            ) : (
                              <>
                                <div className="summary-line">
                                  <span>CGST ({calculations.halfRate}%)</span>
                                  <strong>₹{calculations.cgstAmount.toFixed(2)}</strong>
                                </div>
                                <div className="summary-line">
                                  <span>SGST ({calculations.halfRate}%)</span>
                                  <strong>₹{calculations.sgstAmount.toFixed(2)}</strong>
                                </div>
                              </>
                            )}
                          </>
                        )}
                        <div className="summary-line grand-total-line">
                          <span>Total</span>
                          <strong className="grand-total-val">₹{calculations.grandTotal.toLocaleString()}</strong>
                        </div>
                        {baseAmountPaid > 0 && additionalPayment > 0 && (
                          <>
                            <div className="summary-line">
                              <span>Amount Paid Before</span>
                              <strong>₹{baseAmountPaid.toLocaleString()}</strong>
                            </div>
                            <div className="summary-line">
                              <span>Amount Paid Now</span>
                              <strong style={{ color: "#16a34a" }}>₹{additionalPayment.toLocaleString()}</strong>
                            </div>
                          </>
                        )}
                        {effectiveAmountPaid > 0 && (
                          <div className="summary-line">
                            <span>Total Amount Paid</span>
                            <strong>₹{effectiveAmountPaid.toLocaleString()}</strong>
                          </div>
                        )}
                        {balanceDue > 0 && paymentStatus !== "unpaid" && (
                          <div className="summary-line">
                            <span>Total Balance Due</span>
                            <strong style={{ color: "#dc2626" }}>₹{balanceDue.toLocaleString()}</strong>
                          </div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 4. Footer Stack */}
              <div className="invoice-footer-stack">
                <div className="total-in-words-row">
                  Total amount (in words): <strong>{numberToWordsIndian(calculations.grandTotal)}</strong>
                </div>

                {/* GST Breakdown Table - Adaptive for Inter-State (IGST) vs Intra-State (CGST + SGST) */}
                {hasCustomerGst && !calculations.isExempt && effectiveGstRate > 0 && (
                  <div className="gst-breakdown-section">
                    {effectiveIsInterState ? (
                      <table className="gst-breakdown-table">
                        <thead>
                          <tr>
                            <th rowSpan={2} className="text-center" style={{ verticalAlign: "middle" }}>
                              HSN/SAC
                            </th>
                            <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>
                              TAXABLE VALUE
                            </th>
                            <th colSpan={2} className="text-center border-b">
                              INTEGRATED TAX
                            </th>
                            <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>
                              TOTAL TAX AMOUNT
                            </th>
                          </tr>
                          <tr>
                            <th className="text-center">RATE</th>
                            <th className="text-right">AMOUNT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gstBreakdown.map((row, idx) => (
                            <tr key={row.hsnSac ? `${row.hsnSac}-${row.igstRate}-${idx}` : `gst-${idx}`}>
                              <td className="text-center">{row.hsnSac}</td>
                              <td className="text-right">INR {row.taxableValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-center">{row.igstRate}%</td>
                              <td className="text-right">INR {row.igstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-right">
                                <strong>INR {row.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                              </td>
                            </tr>
                          ))}
                          <tr className="gst-breakdown-total-row">
                            <td className="text-center">
                              <strong>TOTAL</strong>
                            </td>
                            <td className="text-right">
                              <strong>INR {calculations.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                            <td className="text-center">—</td>
                            <td className="text-right">
                              <strong>INR {calculations.igstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                            <td className="text-right">
                              <strong>INR {calculations.totalGst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <table className="gst-breakdown-table">
                        <thead>
                          <tr>
                            <th rowSpan={2} className="text-center" style={{ verticalAlign: "middle" }}>
                              HSN/SAC
                            </th>
                            <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>
                              TAXABLE VALUE
                            </th>
                            <th colSpan={2} className="text-center border-b">
                              CENTRAL TAX
                            </th>
                            <th colSpan={2} className="text-center border-b">
                              STATE TAX
                            </th>
                            <th rowSpan={2} className="text-right" style={{ verticalAlign: "middle" }}>
                              TOTAL TAX AMOUNT
                            </th>
                          </tr>
                          <tr>
                            <th className="text-center">RATE</th>
                            <th className="text-right">AMOUNT</th>
                            <th className="text-center">RATE</th>
                            <th className="text-right">AMOUNT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gstBreakdown.map((row, idx) => (
                            <tr key={row.hsnSac ? `${row.hsnSac}-${row.cgstRate}-${idx}` : `gst-${idx}`}>
                              <td className="text-center">{row.hsnSac}</td>
                              <td className="text-right">INR {row.taxableValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-center">{row.cgstRate}%</td>
                              <td className="text-right">INR {row.cgstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-center">{row.sgstRate}%</td>
                              <td className="text-right">INR {row.sgstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="text-right">
                                <strong>INR {row.totalTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                              </td>
                            </tr>
                          ))}
                          <tr className="gst-breakdown-total-row">
                            <td className="text-center">
                              <strong>TOTAL</strong>
                            </td>
                            <td className="text-right">
                              <strong>INR {calculations.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                            <td className="text-center">—</td>
                            <td className="text-right">
                              <strong>INR {calculations.cgstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                            <td className="text-center">—</td>
                            <td className="text-right">
                              <strong>INR {calculations.sgstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                            <td className="text-right">
                              <strong>INR {calculations.totalGst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                <div className="invoice-grid-footer">
                  <div className="footer-col-bank">
                    <div className="footer-kicker">Bank Details:</div>
                    <dl className="bank-details-list">
                      <dt>Bank:</dt>
                      <dd>{effectiveBankDetails?.bankName || "YES BANK"}</dd>
                      <dt>Account #:</dt>
                      <dd>{effectiveBankDetails?.accountNumber || "66789999222445"}</dd>
                      <dt>IFSC:</dt>
                      <dd>{effectiveBankDetails?.ifscCode || "YESBBIN4567"}</dd>
                      <dt>Branch:</dt>
                      <dd>{effectiveBankDetails?.branchName || "Kodihalli"}</dd>
                    </dl>
                  </div>

                  <div className="footer-col-sig">
                    <span className="sig-header">For {displayCompanyName.toUpperCase()}</span>
                    <div className="sig-stamp-container">
                      <InvoiceCompanySeal sealUrl={dbCompanySeal} sealLoadError={sealLoadError} onSealError={() => setSealLoadError(true)} displayCompanyName={displayCompanyName} />
                    </div>
                    <span className="sig-footer">Authorized Signatory (Digital Signature)</span>
                  </div>
                </div>

                <div className="invoice-notes-terms-grid">
                  <div className="notes-block">
                    <div className="notes-kicker">Notes:</div>
                    <p>{invoiceNotes || "Thank you for the Business"}</p>
                  </div>
                  {dbTerms.length > 0 && (
                    <div className="terms-block">
                      <div className="terms-kicker">Terms and Conditions:</div>
                      <ol>
                        {dbTerms.map((t, idx) => (
                          <li key={`term-${t.slice(0, 15)}-${idx}`}>{t}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>

                <div className="invoice-bottom-page-footer">
                  <span>Page 1/1</span>
                  <span>This is a digitally signed document.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Bottom Action Row */}
        <div className="invoice-modal-actions">
          <Button onClick={onClose}>Cancel</Button>

          <div style={{ display: "flex", gap: 12 }}>
            <Button icon={<MailOutlined />} onClick={() => handlePrint("email")}>
              Send Mail
            </Button>
            <Button icon={<PrinterOutlined />} onClick={() => handlePrint("print")}>
              Print Invoice
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveInvoice} loading={isSaving} disabled={invoiceItems.length === 0} style={{ background: "#2563eb", fontWeight: 600 }}>
              {activeRecord ? "Save Changes" : "Save Invoice"}
            </Button>
          </div>
        </div>

        {/* Mail Compose Drawer with Invoice Attachment */}
        <MailComposeDrawer
          open={mailDrawerOpen}
          visible={mailDrawerOpen}
          onClose={() => {
            setMailDrawerOpen(false);
            setMailPrefillData(null);
          }}
          lead={activeLead}
          prefillData={mailPrefillData}
          companyCode={documentCompanyCode || activeCompanyCode}
          employeeInfo={{ id: activeEmployeeId, name: activeEmployeeName, companyName: displayCompanyName }}
          onSuccess={() => {
            message.success("Invoice sent via email!");
          }}
        />
      </div>
    </div>
  );
}

export default InvoiceModal;
